import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useWeekdayActivityCount } from "./hooks/useWeekdayActivityCount";
import { useAutoLoad } from '../../../hooks/useAutoLoad';
import BaseTile from '../../../components/Tile/BaseTile';
import TileHeader, { TileHeaderButton } from '../../../components/Tile/TileHeader';
import TileContent, { TileChartContent, TileStatusContent } from '../../../components/Tile/TileContent';
import RefreshButton from '../../../components/Tile/RefreshButton';
import { cn } from '../../../utils/cn';

// Custom Tooltip Component
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const { median } = payload[0].payload;
    return (
      <div className="bg-white border border-gray-300 p-2 rounded shadow-sm">
        <p className="font-medium text-gray-800">{label}</p>
        <p className="text-sm text-gray-600">Median Count: {median}</p>
      </div>
    );
  }
  return null;
};

// Dropdown Controls Component
const DropdownControls = ({
  objectTypes,
  objectTypeActivities,
  selectedObjectType,
  selectedActivity,
  setSelectedObjectType,
  setSelectedActivity
}) => (
  <div className="bg-gray-50 border border-gray-200 rounded-md p-4 space-y-4">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Object Type Dropdown */}
      <div className="flex flex-col min-w-[200px]">
        <label className="text-sm font-medium text-gray-700 mb-1">
          Select Object Type:
        </label>
        <select
          value={selectedObjectType}
          onChange={(e) => {
            setSelectedObjectType(e.target.value);
            setSelectedActivity("");
          }}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="">-- Select Object Type --</option>
          {objectTypes.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </div>

      {/* Activity Dropdown */}
      <div className="flex flex-col min-w-[200px]">
        <label className="text-sm font-medium text-gray-700 mb-1">
          Select Activity:
        </label>
        <select
          value={selectedActivity}
          onChange={(e) => setSelectedActivity(e.target.value)}
          disabled={!selectedObjectType}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          <option value="">-- Select Activity --</option>
          {(objectTypeActivities[selectedObjectType] || []).map((activity) => (
            <option key={activity} value={activity}>
              {activity}
            </option>
          ))}
        </select>
      </div>
    </div>
  </div>
);

function WeekdayActivityCount({ activeFile, activityName, objectType, onRefresh }) {
  const {
    objectTypes,
    objectTypeActivities,
    selectedObjectType,
    selectedActivity,
    setSelectedObjectType,
    setSelectedActivity,
    dropdownVisible,
    setDropdownVisible,
    chartData,
    loading,
    error,
    isMetadataLoaded,
    fetchMetadata,
  } = useWeekdayActivityCount(activeFile);

  const chartTitle = selectedActivity && selectedObjectType
    ? `Weekly Activity Pattern for '${selectedActivity}' on '${selectedObjectType}'`
    : 'Weekly Activity Pattern';

  const showLoadDataPrompt = !isMetadataLoaded && !loading && !error;
  const showChart = isMetadataLoaded && !loading && !error && selectedActivity && selectedObjectType && chartData.length > 0 && chartData.some((item) => item.median > 0);
  const showNoData = isMetadataLoaded && !loading && !error && selectedActivity && selectedObjectType && (!chartData.length || !chartData.some((item) => item.median > 0));
  const showSelectionPrompt = isMetadataLoaded && !loading && !error && (!selectedActivity || !selectedObjectType);
  const hasData = showChart;

  // Auto-load data when persistent cache is enabled
  useAutoLoad(fetchMetadata, [activeFile], hasData);

  // Provide refresh function to parent (GridComponent)
  React.useEffect(() => {
    if (onRefresh) {
      onRefresh(fetchMetadata);
    }
  }, [onRefresh, fetchMetadata]);

  return (
    <BaseTile
      loading={loading}
      error={error}
      minHeight="lg"
      className="h-full"
    >
      {isMetadataLoaded && (
        <TileHeader 
          layout="between" 
          size="default"
          actions={
            <TileHeaderButton
              variant="primary"
              size="sm"
              onClick={() => setDropdownVisible(!dropdownVisible)}
              className="bg-success-500 hover:bg-success-600 focus:ring-success-500"
            >
              {dropdownVisible ? "Hide Options" : "Show Options"}
            </TileHeaderButton>
          }
        />
      )}

      <TileContent layout="flex" direction="column" gap="lg" grow>
        {showLoadDataPrompt && (
          <TileStatusContent
            status="info"
            title="Load Activity Data"
            message="Click the button to load the available object types and activities to build the chart."
            action={
              <button
                onClick={fetchMetadata}
                className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                Load Data
              </button>
            }
            icon={
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10m0-10l8 6m8-6v10m0-10l-8 6m-4 4h16" />
              </svg>
            }
          />
        )}

        {isMetadataLoaded && dropdownVisible && (
          <DropdownControls
            objectTypes={objectTypes}
            objectTypeActivities={objectTypeActivities}
            selectedObjectType={selectedObjectType}
            selectedActivity={selectedActivity}
            setSelectedObjectType={setSelectedObjectType}
            setSelectedActivity={setSelectedActivity}
          />
        )}

        {showChart && (
          <TileChartContent height={300} className="flex-grow">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="weekday" />
                <YAxis
                  label={{
                    value: "Median Count",
                    angle: -90,
                    position: "insideLeft",
                  }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="median" fill="#8884d8" name="Median Activities" />
              </BarChart>
            </ResponsiveContainer>
          </TileChartContent>
        )}

        {showNoData && (
          <TileStatusContent
            status="empty"
            title="No Data Available"
            message="No data found for the selected activity and object type combination."
            icon={
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            }
          />
        )}

        {showSelectionPrompt && (
          <TileStatusContent
            status="empty"
            title="Select Options to View Chart"
            message="Please select both an object type and activity from the options above to view the weekly activity pattern."
            action={
              !dropdownVisible && (
                <button
                  onClick={() => setDropdownVisible(true)}
                  className="px-4 py-2 bg-success-500 text-white text-sm font-medium rounded-md hover:bg-success-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-success-500 focus:ring-offset-2"
                >
                  Show Options
                </button>
              )
            }
            icon={
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
                      d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
              </svg>
            }
          />
        )}
      </TileContent>
    </BaseTile>
  );
}

export default WeekdayActivityCount;